import {
  CollectionTypeEnum,
  HostedApiHostedCampaignsControllerCampaignGeneratorRequest,
  HostedApiHostedCampaignsControllerGetCampaignRequest,
  HostedApiHostedCampaignsControllerOverviewRequest,
  HostedCampaign,
  ValidationError,
} from '@givz/core-client';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  useCoreClientMutation,
  UseCoreClientMutationParams,
  useCoreClientQuery,
} from './client';
import {
  useCreateHandpickedCollection,
  useCuratedCollection,
  useUpdateHandpickedCollection,
} from './collections';

export const incentivesKeys = {
  base: ['incentives'] as const,
  get: (params: HostedApiHostedCampaignsControllerGetCampaignRequest) =>
    [...incentivesKeys.base, 'get', params] as const,
  overview: (params: HostedApiHostedCampaignsControllerOverviewRequest) =>
    [...incentivesKeys.base, 'overview', params] as const,
};

export const useCreateGenerator = (
  params?: Partial<
    UseCoreClientMutationParams<
      HostedCampaign,
      unknown,
      HostedApiHostedCampaignsControllerCampaignGeneratorRequest
    >
  >,
) => {
  return useCoreClientMutation({
    ...params,
    mutationFn: (client) => client.Hosted.campaigns.generator,
  });
};

export const useGetIncentive = (
  params: HostedApiHostedCampaignsControllerGetCampaignRequest,
) =>
  useCoreClientQuery({
    queryFn: (client) => (_queryContext) => client.Hosted.campaigns.get(params),
    queryKey: incentivesKeys.get(params),
    staleTime: Infinity,
  });

export const useIncentiveOverview = (
  params: HostedApiHostedCampaignsControllerOverviewRequest,
) =>
  useCoreClientQuery({
    queryFn: (client) => (_queryContext) =>
      client.Hosted.campaigns.overview(params),
    queryKey: incentivesKeys.overview(params),
  });

export const useUpdateIncentive = () => {
  const queryClient = useQueryClient();

  const onSuccess = (data: HostedCampaign) => {
    queryClient.invalidateQueries(
      incentivesKeys.get({ body: {}, gid: data.gid }),
    );
  };

  return useCoreClientMutation({
    mutationFn: (client) => client.Hosted.campaigns.update,
    onSuccess,
  });
};

export const useActivateIncentive = () => {
  const queryClient = useQueryClient();

  const onSuccess = (data: HostedCampaign) => {
    queryClient.invalidateQueries(
      incentivesKeys.get({ body: {}, gid: data.gid }),
    );
  };

  return useCoreClientMutation({
    mutationFn: (client) => client.Hosted.campaigns.activate,
    onSuccess,
  });
};

export const useDeactivateIncentive = () => {
  const queryClient = useQueryClient();

  const onSuccess = (data: HostedCampaign) => {
    queryClient.invalidateQueries(
      incentivesKeys.get({ body: {}, gid: data.gid }),
    );
  };

  return useCoreClientMutation({
    mutationFn: (client) => client.Hosted.campaigns.deactivate,
    onSuccess,
  });
};

export const useUpdateCollectionFromIncentive = (incentiveGid: string) => {
  const incentiveQuery = useGetIncentive({ body: {}, gid: incentiveGid });
  const curatedCollectionQuery = useCuratedCollection();
  const updateIncentiveMutation = useUpdateIncentive();
  const createHandpickedCollectionMutation = useCreateHandpickedCollection();
  const updateHandpickedCollectionMutation = useUpdateHandpickedCollection();
  const [isLoading, setIsLoading] = useState(false);

  async function updateCollection(params: {
    collectionType: CollectionTypeEnum;
    organizationGids: string[];
    onUpdated?: () => void;
  }) {
    const { collectionType, onUpdated, organizationGids } = params;
    const activeIncentive = incentiveQuery.data?.data;
    const curatedCollection = curatedCollectionQuery.data?.data;

    setIsLoading(true);

    const activeIncentiveCollectionType =
      activeIncentive?.collection.type ?? CollectionTypeEnum.Handpicked;

    if (
      !activeIncentive ||
      (collectionType === CollectionTypeEnum.Curated &&
        activeIncentiveCollectionType === CollectionTypeEnum.Curated)
    ) {
      return;
    }

    let responseErrors: ValidationError[] | null = null;

    // if incentive.collection.type === curated and selected is handpicked then create
    if (
      collectionType === CollectionTypeEnum.Handpicked &&
      activeIncentiveCollectionType === CollectionTypeEnum.Curated
    ) {
      const { data, errors } =
        await createHandpickedCollectionMutation.mutateAsync({
          collectionCreate: {
            organizationGids,
          },
        });

      responseErrors = errors;

      if (data && !errors) {
        updateIncentiveMutation.mutate({
          gid: activeIncentive.gid,
          hostedCampaignGeneratorUpdate: {
            collectionGid: data.gid,
          },
        });
      }
    }

    // if incentive.collection.type === handpicked and selected is handpicked then update
    if (
      collectionType === CollectionTypeEnum.Handpicked &&
      activeIncentiveCollectionType === CollectionTypeEnum.Handpicked
    ) {
      const { errors } = await updateHandpickedCollectionMutation.mutateAsync({
        collectionUpdate: {
          organizationGids,
        },
        gid: activeIncentive.collection.gid,
      });

      responseErrors = errors;
    }

    // if incentive.collection.type === handpicked and selected is curated then update incentive
    if (
      collectionType === CollectionTypeEnum.Curated &&
      activeIncentiveCollectionType === CollectionTypeEnum.Handpicked
    ) {
      updateIncentiveMutation.mutate({
        gid: activeIncentive.gid,
        hostedCampaignGeneratorUpdate: {
          collectionGid: curatedCollection?.gid,
        },
      });
    }

    if (!responseErrors && onUpdated) {
      onUpdated();
    }

    setIsLoading(false);
  }

  return {
    isLoading,
    updateCollection,
  };
};
